import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import {
  ShieldCheckIcon,
  ChartBarIcon,
  UsersIcon,
  BanknotesIcon,
  BoltIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import ThemeSwitch from "./ThemeSwitch";
import logo from "../logo.svg";
import { exchangeAdminSession } from "../api/adminSessionAPI";

const SESSION_EXPIRES_KEY = "admin_session_expires_at";

// Best-effort JWT expiry — decodes exp locally without verifying signature.
// Backend still validates; this is only for skipping the Google tokeninfo
// bounce when our session token is still valid.
function jwtExpiresAt(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

const CLIENT_ID =
  "237397542353-rgp0jg95rs8geiu3e8gbepgfni8fp63a.apps.googleusercontent.com";

const FEATURES = [
  {
    icon: UsersIcon,
    title: "Users & moderation",
    desc: "Юзерлерді, компанияларды және хабарландыруларды бір жерден басқару.",
  },
  {
    icon: BanknotesIcon,
    title: "Finance",
    desc: "Withdrawals, KYC, dealer commission — бір click-те approve/reject.",
  },
  {
    icon: ChartBarIcon,
    title: "Analytics",
    desc: "Marketplace live-KPI: сатылымдар, promotion, чат жүктемесі.",
  },
  {
    icon: BoltIcon,
    title: "Real-time chat",
    desc: "Operator queue, каненый жауап, transfer және internal notes.",
  },
];

const SignIn = ({ onSignIn }) => {
  const [status, setStatus] = useState("idle"); // idle | verifying | error | success
  const [errorMessage, setErrorMessage] = useState("");

  const verifyGoogleToken = useCallback((token) => {
    const url = `https://www.googleapis.com/oauth2/v3/tokeninfo?id_token=${token}`;
    return fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (!data?.sub) throw new Error(data.error_description || "Токен жарамсыз");
        return data;
      });
  }, []);

  // On mount: if the stored token is our long-lived admin-session token and
  // still in its 24h window, trust it and skip the Google tokeninfo bounce
  // (which would fail — the token isn't a Google JWT).
  useEffect(() => {
    const token = localStorage.getItem("google_id_token");
    if (!token) return;
    const localExp = jwtExpiresAt(token);
    if (localExp && localExp > Date.now()) {
      setStatus("success");
      onSignIn();
      return;
    }
    // Token is either expired or unparsable → clear and require re-login.
    localStorage.removeItem("google_id_token");
    localStorage.removeItem(SESSION_EXPIRES_KEY);
  }, [onSignIn]);

  const handleGoogleSignIn = async (response) => {
    if (!response?.credential) {
      setStatus("error");
      setErrorMessage("Google жауап бермеді");
      return;
    }
    setStatus("verifying");
    setErrorMessage("");
    try {
      await verifyGoogleToken(response.credential);
    } catch (err) {
      console.error("[signin] google tokeninfo failed", err);
      localStorage.removeItem("google_id_token");
      localStorage.removeItem(SESSION_EXPIRES_KEY);
      setStatus("error");
      setErrorMessage(err?.message || "Google login сәтсіз аяқталды");
      return;
    }

    // Google token is valid — user is definitely allowed to proceed. Save it
    // immediately as the fallback so a hiccup below doesn't block sign-in.
    localStorage.setItem("google_id_token", response.credential);
    localStorage.removeItem(SESSION_EXPIRES_KEY);

    // Best-effort: swap the 1h Google JWT for our 24h admin-session token.
    // If the exchange fails (backend cold-start, network hiccup, temporary
    // 5xx), we keep the Google JWT and the user is still signed in — just
    // for 1h instead of 24h.
    try {
      const session = await exchangeAdminSession(response.credential);
      if (session?.token) {
        localStorage.setItem("google_id_token", session.token);
        if (session.expires_at) {
          localStorage.setItem(SESSION_EXPIRES_KEY, session.expires_at);
        }
      }
    } catch (err) {
      console.warn("[signin] session exchange failed, using 1h Google JWT", err);
    }
    setStatus("success");
    onSignIn();
  };

  const host = useMemo(() => (typeof window !== "undefined" ? window.location.host : ""), []);

  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <div className="sign-in-container min-h-screen w-full flex flex-col md:flex-row overflow-hidden">
        {/* LEFT: hero / brand */}
        <div className="hidden md:flex flex-col justify-between w-1/2 lg:w-3/5 p-10 lg:p-14 relative overflow-hidden">
          {/* animated gradient blobs */}
          <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-green-500/20 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-blue-500/15 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:20px_20px]" />

          <div className="relative z-10 flex items-center gap-3">
            <img src={logo} alt="Agroland" className="w-11 h-11 rounded-xl bg-white/10 p-1.5" />
            <div>
              <div className="text-white text-lg font-bold leading-none">Agroland</div>
              <div className="text-text-secondary text-xs mt-0.5">Admin console</div>
            </div>
          </div>

          <div className="relative z-10">
            <h1 className="text-white text-3xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Ауылшаруашылық <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-300">
                маркетплейсінің
              </span>{" "}
              командалық орталығы
            </h1>
            <p className="text-text-secondary text-base lg:text-lg max-w-lg mb-10">
              Бір панельден дилер қаржысын, KYC верификациясын, оператор чатын және
              контент модерациясын басқар.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="dark-card border border-white/5 rounded-xl p-4 flex gap-3 hover:border-green-500/40 transition-colors"
                >
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-accent-grad flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-semibold">{title}</div>
                    <div className="text-text-secondary text-xs mt-0.5 line-clamp-2">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-2 text-xs text-text-secondary">
            <ShieldCheckIcon className="w-4 h-4" />
            SSL · Google OAuth · role-based access
          </div>
        </div>

        {/* RIGHT: sign-in card */}
        <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 md:py-16 relative">
          <div className="absolute top-4 right-4 z-10">
            <ThemeSwitch />
          </div>

          <div className="w-full max-w-md">
            {/* Mobile brand */}
            <div className="md:hidden flex items-center justify-center gap-3 mb-8">
              <img src={logo} alt="Agroland" className="w-10 h-10" />
              <div className="text-white text-lg font-bold">Agroland admin</div>
            </div>

            <div className="dark-card rounded-2xl p-8 shadow-2xl border border-white/5">
              <div className="mb-6">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-accent-grad flex items-center justify-center mb-4 shadow-lg">
                  <LockClosedIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-center text-white text-2xl font-bold">
                  Панельге кіру
                </h2>
                <p className="text-center text-text-secondary text-sm mt-2">
                  Тек Agroland жеке Google аккаунттарына рұқсат.
                </p>
              </div>

              {status === "error" && errorMessage && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {errorMessage}
                </div>
              )}

              <div className="flex justify-center">
                {status === "verifying" ? (
                  <div className="flex items-center gap-3 text-white text-sm py-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Google арқылы тексеру...
                  </div>
                ) : (
                  <div className="[&>div]:!w-full">
                    <GoogleLogin
                      onSuccess={handleGoogleSignIn}
                      theme="filled_blue"
                      size="large"
                      text="signin_with"
                      shape="pill"
                      width="320"
                      onError={() => {
                        setStatus("error");
                        setErrorMessage("Google login сәтсіз аяқталды");
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 pt-5 border-t border-white/5 text-center text-xs text-text-secondary">
                Кірген домен: <span className="text-white/80">{host}</span>
              </div>
            </div>

            <div className="mt-6 text-center text-xs text-text-secondary">
              © {new Date().getFullYear()} Agroland ·{" "}
              <a
                href="https://agroland.kz"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                agroland.kz
              </a>
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default SignIn;
